// src/student/context/StudentAuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { studentPortalAPI } from "../services/studentApi";

const StudentAuthContext = createContext(null);

const DEFAULT_STUDENT = {
  name: "Priya Sharma",
  email: "priya.sharma@example.com",
  phone: "+91 98401 22334",
  registrationId: "SIA-2026-1042",
};

export const StudentAuthProvider = ({ children }) => {
  const [isStudentAuthenticated, setIsStudentAuthenticated] = useState(() => {
    return localStorage.getItem("selvas_student_authenticated") === "true";
  });

  const [studentUser, setStudentUser] = useState(() => {
    const saved = localStorage.getItem("selvas_student_user");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return DEFAULT_STUDENT;
      }
    }
    return DEFAULT_STUDENT;
  });

  const [authError, setAuthError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Sync state to localStorage
  useEffect(() => {
    if (isStudentAuthenticated) {
      localStorage.setItem("selvas_student_authenticated", "true");
      localStorage.setItem("selvas_student_user", JSON.stringify(studentUser));
    } else {
      localStorage.removeItem("selvas_student_authenticated");
    }
  }, [isStudentAuthenticated, studentUser]);

  const studentLogin = async (email, password) => {
    setAuthError("");
    setIsLoading(true);
    const trimmedEmail = (email || "").trim().toLowerCase();
    const trimmedPassword = (password || "").trim();

    try {
      const res = await studentPortalAPI.login(trimmedEmail, trimmedPassword);
      if (res.success && res.student) {
        if (res.token) {
          localStorage.setItem("selvas_student_token", res.token);
        }
        setStudentUser(res.student);
        setIsStudentAuthenticated(true);
        setIsLoading(false);
        return { success: true, student: res.student };
      }
    } catch (apiError) {
      console.warn("Backend student login error, verifying local fallback:", apiError.message);
      // Demo fallback for initial seeded student
      if (trimmedEmail === "priya.sharma@example.com" && (trimmedPassword === "password123" || trimmedPassword === "12345")) {
        setStudentUser(DEFAULT_STUDENT);
        setIsStudentAuthenticated(true);
        setIsLoading(false);
        return { success: true, student: DEFAULT_STUDENT };
      }

      const msg = apiError.message || "Invalid email or password.";
      setAuthError(msg);
      setIsLoading(false);
      return { success: false, error: msg };
    }
  };

  const studentRegister = async (name, email, password, phone) => {
    setAuthError("");
    setIsLoading(true);
    try {
      const res = await studentPortalAPI.register({
        name,
        email: (email || "").trim().toLowerCase(),
        password,
        phone,
      });
      if (res.success && res.student) {
        if (res.token) {
          localStorage.setItem("selvas_student_token", res.token);
        }
        setStudentUser(res.student);
        setIsStudentAuthenticated(true);
        setIsLoading(false);
        return { success: true, student: res.student };
      }
    } catch (err) {
      const msg = err.message || "Registration failed. Please check your details.";
      setAuthError(msg);
      setIsLoading(false);
      return { success: false, error: msg };
    }
  };

  const studentLogout = () => {
    setIsStudentAuthenticated(false);
    localStorage.removeItem("selvas_student_authenticated");
    localStorage.removeItem("selvas_student_token");
    setAuthError("");
  };

  const updateProfile = async (updates) => {
    try {
      const res = await studentPortalAPI.updateProfile(updates);
      if (res.success && res.student) {
        setStudentUser(res.student);
        localStorage.setItem("selvas_student_user", JSON.stringify(res.student));
        return { success: true };
      }
    } catch (e) {
      console.warn("Failed to update profile on backend:", e.message);
    }

    setStudentUser((prev) => {
      const updated = { ...prev, ...updates };
      localStorage.setItem("selvas_student_user", JSON.stringify(updated));
      return updated;
    });
    return { success: true };
  };

  return (
    <StudentAuthContext.Provider
      value={{
        isStudentAuthenticated,
        studentUser,
        authError,
        isLoading,
        setAuthError,
        studentLogin,
        studentRegister,
        studentLogout,
        updateProfile,
      }}
    >
      {children}
    </StudentAuthContext.Provider>
  );
};

export const useStudentAuth = () => {
  const context = useContext(StudentAuthContext);
  if (!context) {
    throw new Error("useStudentAuth must be used within a StudentAuthProvider");
  }
  return context;
};
